import SignificanceBadge from './SignificanceBadge';
import clsx from 'clsx';

interface RiskSummary {
  aspectOfCredit: string;
  riskFactor: string;
  riskFactorLabel: string;
  product: string;
  significanceLevel: string;
  pbgVolume: string;
  pbgLabel: string;
  recommendation: 'Focal Point' | 'Targeted Review' | 'None';
}

const SECTIONS = [
  { title: 'I. Focal Points', action: 'Focal Point' },
  { title: 'II. Targeted Reviews', action: 'Targeted Review' },
] as const;

interface FldSummaryTabProps {
  risks: RiskSummary[];
}

export default function FldSummaryTab({ risks }: FldSummaryTabProps) {
  if (risks.length === 0) {
    return (
      <div className="text-center py-12">
        <p className="text-green-600 font-medium">No statistically significant disparities identified.</p>
        <p className="text-ink-3 text-sm mt-1">All risk factors within acceptable thresholds.</p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full">
        <thead>
          <tr className="border-b border-rule">
            <th className="text-left py-3 px-3 text-xs font-semibold text-ink-3 uppercase tracking-wider">Aspect</th>
            <th className="text-left py-3 px-3 text-xs font-semibold text-ink-3 uppercase tracking-wider">Risk Factor</th>
            <th className="text-left py-3 px-3 text-xs font-semibold text-ink-3 uppercase tracking-wider">Product</th>
            <th className="text-center py-3 px-3 text-xs font-semibold text-ink-3 uppercase tracking-wider">Significance</th>
            <th className="text-left py-3 px-3 text-xs font-semibold text-ink-3 uppercase tracking-wider">PBG</th>
            <th className="text-left py-3 px-3 text-xs font-semibold text-ink-3 uppercase tracking-wider">Volume</th>
            <th className="text-left py-3 px-3 text-xs font-semibold text-ink-3 uppercase tracking-wider">Action</th>
          </tr>
        </thead>
        {/* Focal Points (I.) then Targeted Reviews (II.), each in the order of the screens; the API
            returns the rows already in Michael Wallace's order (2026-10-22). */}
        {SECTIONS.map(({ title, action }) => {
          const rows = risks.filter((r) => r.recommendation === action);
          if (rows.length === 0) return null;
          return (
        <tbody key={action}>
          <tr className="border-b border-rule bg-panel-2">
            <td colSpan={7} className="py-2 px-3 text-center text-sm font-semibold text-ink">{title}</td>
          </tr>
          {rows.map((risk, i) => (
            <tr key={i} className={clsx('border-b border-rule', i % 2 === 0 ? 'bg-panel' : 'bg-panel-2')}>
              <td className="py-2.5 px-3 text-sm text-ink-2">{risk.aspectOfCredit}</td>
              <td className="py-2.5 px-3 text-sm text-ink font-medium">{risk.riskFactorLabel}</td>
              <td className="py-2.5 px-3 text-sm text-ink-2">{risk.product}</td>
              <td className="py-2.5 px-3 text-center">
                <SignificanceBadge level={risk.significanceLevel as any} />
              </td>
              <td className="py-2.5 px-3 text-sm text-ink-2">{risk.pbgLabel}</td>
              <td className="py-2.5 px-3 text-sm text-ink-3">{risk.pbgVolume}</td>
              <td className="py-2.5 px-3">
                <span
                  className={clsx(
                    'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold',
                    risk.recommendation === 'Focal Point'
                      ? 'bg-red-100 text-red-800'
                      : risk.recommendation === 'Targeted Review'
                        ? 'bg-amber-100 text-amber-800'
                        : 'bg-panel-2 text-ink-3',
                  )}
                >
                  {risk.recommendation}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
          );
        })}
      </table>
    </div>
  );
}
